import { Form, Formik, type FormikHelpers } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AddMerchantStoreSchema } from '../store.schema';
import CustomTextInput from '../../../components/Form/custom-text-input';
import { extractErrorsFromApi } from '../../../common/helpers';
import { type MerchantStoreRequest } from '../../../common/types';
import RenderErrorBag from '../../../components/render-error-bag';
import { toast } from 'react-toastify';
import { useAddStoreMutation } from '../store.api-slice';

const initialStoreData: MerchantStoreRequest = {
  name: 'Demo Store',
  email: 'demo@example.com',
  phone: '0212346789',
  address: '200 Wynward Street'
};

interface IProps {
  merchantId?: string;
}

const AddStore: React.FC<IProps> = ({ merchantId }) => {
  const [createMerchantStore, { data, isSuccess, isLoading, isError, error }] = useAddStoreMutation();
  const [errorBag, setErrorBag] = useState<string[]>([]);
  const navigate = useNavigate();

  const handleAddStore = async (
    values: MerchantStoreRequest,
    actions: FormikHelpers<MerchantStoreRequest>
  ): Promise<any> => {
    const storeDto = merchantId != null ? { ...values, merchantId } : { ...values };
    await createMerchantStore(storeDto);
    actions.resetForm();
  };

  useEffect(() => {
    if (isSuccess) {
      const message = 'Merchant store is successfully created.';
      toast(message);
      navigate('/admin/stores');
    }

    if (isError && error != null) {
      setErrorBag(extractErrorsFromApi(error));
    }
  }, [data, error, isError, isLoading, isSuccess, navigate]);

  return (
    <div className="mx-auto max-w-2xl py-8 px-8 my-8 rounded-md flex flex-col items-start bg-gray-50 min-h-[70vh]">
      <div className="w-full flex justify-center items-center">
        <span className="w-full h-full text-2xl">Add New Store</span>
        <Link to="/admin/stores" className="w-1/4 text-white text-center p-2 rounded-lg bg-[#1D8A43]">
          <button>List Stores</button>
        </Link>
      </div>
      <div className="w-full flex flex-col items-start px-2 mt-4">
        {errorBag.length > 0 && <RenderErrorBag errorBag={errorBag} />}
        {!isSuccess && (
          <Formik initialValues={initialStoreData} onSubmit={handleAddStore} validationSchema={AddMerchantStoreSchema}>
            {({ isSubmitting }) => (
              <Form className="w-full flex flex-col items-start px-2" autoComplete="off">
                <CustomTextInput
                  type="text"
                  label="Store Name"
                  name="name"
                  divClass="w-full flex flex-col "
                  labelClass="w-full text-left text-gray-600"
                  inputClass="w-full my-1 px-4 py-1 rounded-md"
                />
                <CustomTextInput
                  type="text"
                  label="Email"
                  name="email"
                  divClass="w-full flex flex-col"
                  labelClass="w-full text-left mt-2 text-gray-600"
                  inputClass="w-full my-1 px-4 py-1 rounded-md"
                />
                <CustomTextInput
                  type="text"
                  label="Phone"
                  name="phone"
                  divClass="w-full flex flex-col"
                  labelClass="w-full text-left mt-2 text-gray-600"
                  inputClass="w-full my-1 px-4 py-1 rounded-md"
                />
                <CustomTextInput
                  type="text"
                  label="Address"
                  name="address"
                  divClass="w-full flex flex-col"
                  labelClass="w-full text-left mt-2 text-gray-600"
                  inputClass="w-full my-1 px-4 py-1 rounded-md"
                />
                <div className="w-full flex space-x-2 mt-4">
                  <button
                    type="submit"
                    disabled={isLoading || isSubmitting}
                    className="w-1/2 text-white px-4 py-2 rounded-lg bg-[#1D8A43]"
                  >
                    Submit
                  </button>
                  <Link to="/admin/stores" className="w-1/2">
                    <button className="w-full text-black ml-4 px-5 py-2 rounded-lg bg-gray-200">Cancel</button>
                  </Link>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </div>
  );
};

export default AddStore;
